import type { CreasePattern } from '@/origami/types';
import { patternToFold, foldToPattern, parseFold } from '@/io/fold';

/**
 * Shareable URLs.
 *
 * The pattern is serialised as FOLD, deflated where the platform offers
 * CompressionStream, and base64url-encoded into the `model` query parameter.
 * URLs have practical length limits, so anything over ~6 KB encoded is refused
 * rather than silently producing a link that some clients will truncate.
 */
const MAX_ENCODED_LENGTH = 6000;

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (text: string): Uint8Array => {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
};

async function deflate(text: string): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function inflate(bytes: Uint8Array): Promise<string | null> {
  if (typeof DecompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([bytes as unknown as BlobPart])
      .stream()
      .pipeThrough(new DecompressionStream('deflate-raw'));
    return await new Response(stream).text();
  } catch {
    return null;
  }
}

export interface EncodeResult {
  url: string | null;
  error: string | null;
}

export async function encodePatternToUrl(
  pattern: CreasePattern,
  base = typeof location !== 'undefined' ? location.href.split('?')[0] : '/',
): Promise<EncodeResult> {
  const json = JSON.stringify(patternToFold(pattern));
  const compressed = await deflate(json);
  const payload = compressed
    ? `z${toBase64Url(compressed)}`
    : `r${toBase64Url(new TextEncoder().encode(json))}`;
  if (payload.length > MAX_ENCODED_LENGTH) {
    return {
      url: null,
      error: `This pattern encodes to ${payload.length} characters, over the ${MAX_ENCODED_LENGTH} character limit for a shareable link. Export a .fold file instead.`,
    };
  }
  return { url: `${base}?model=${payload}`, error: null };
}

export async function decodePatternFromUrl(search: string): Promise<CreasePattern | null> {
  const params = new URLSearchParams(search);
  const payload = params.get('model');
  if (!payload) return null;
  try {
    const bytes = fromBase64Url(payload.slice(1));
    const json =
      payload[0] === 'z' ? await inflate(bytes) : new TextDecoder().decode(bytes);
    if (!json) return null;
    const { file } = parseFold(json);
    if (!file) return null;
    return foldToPattern(file).pattern;
  } catch {
    return null;
  }
}
